import { WithStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import withStyles from "@material-ui/core/styles/withStyles";
import { isNil } from "lodash";
import * as moment from "moment";
import * as React from "react";

import { getProductHelper } from "../../helper/functions.helper";
import { User } from "../../models/User.model";
import { theme } from "../../theme/theme";
import { Customer } from "../../types/Customer";
import { ProductName, Products, ProductVariantName } from "../../types/Products";
import { Quotation } from "../../types/Quotation";
import { quotationFormStyles } from "./QuotationForm.styles";
import QuotationFormAdvisor from "./QuotationFormAdvisor";
import QuotationFormCustomer from "./QuotationFormCustomer";
import QuotationFormDate from "./QuotationFormDate";
import QuotationFormProductAA from "./QuotationFormProductAA";
import QuotationFormProductGO from "./QuotationFormProductGO";
import QuotationFormProductGPI from "./QuotationFormProductGPI";
import QuotationFormProductSPR from "./QuotationFormProductSPR";
import { NotificationItem } from "../../types/NotificationItem";
import Notification from "../Notification/Notification";
import { ProductHelper } from "../../helper/Product.helper";
import { ProductAAHelper } from "../../helper/ProductAA.helper";
import { ProductSPRHelper } from "../../helper/ProductSPR.helper";
import { ProductGOHelper } from "../../helper/ProductGO.helper";
import { ProductGPIHelper } from "../../helper/ProductGPI.helper";

interface QuotationFormProps extends WithStyles<typeof quotationFormStyles> {
    productName: ProductName;
    productVariantName: ProductVariantName;
    customers: Customer[];
    advisor: User;
    onValidate(customer: Customer, quotation: Quotation, isCustomerModifiedOrNew?: boolean);
}

type QuotationFormState = {
    customer: Customer;
    quotation: Quotation;
    isCustomerModifiedOrNew?: boolean;
    notification: NotificationItem;
};

class QuotationForm extends React.Component<QuotationFormProps, QuotationFormState> {
    private productHelper: ProductHelper = null;

    public async componentWillMount() {
        await this.init();
    }

    public onAdvisorChange(validityDate: string) {
        const quotation = { ...this.state.quotation, validityDate };
        this.setState({ quotation });
        this.productHelper.setQuotation(quotation);
    }

    public onCustomerChange(customer: Customer, isCustomerModifiedOrNew?: boolean) {
        this.setState({ customer, isCustomerModifiedOrNew });
        this.productHelper.setCustomer(customer);
    }

    public onDateChange(effectiveDate: string) {
        const quotation = { ...this.state.quotation, effectiveDate };
        this.setState({ quotation });
        this.productHelper.setQuotation(quotation);
    }

    public onProductChange(product: Products) {
        const quotation = { ...this.state.quotation, product: product };
        this.setState({ quotation });
        this.productHelper.setQuotation(quotation);
    }

    public async onSubmit(event) {
        event.preventDefault();
        this.props.onValidate(this.state.customer, this.state.quotation, this.state.isCustomerModifiedOrNew);
    }

    public renderProductForm() {
        switch (this.props.productName) {
            case "AA":
                return (
                    <QuotationFormProductAA
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductAAHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}                        
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "SPR":
                return (
                    <QuotationFormProductSPR
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductSPRHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "GO":
                return (
                    <QuotationFormProductGO
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductGOHelper}
                        productVariantName={this.props.productVariantName}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "GPI":
                return (
                    <QuotationFormProductGPI
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductGPIHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            default:
                return null;
        }
    }

    public render() {
        if (!this.state) {
            return null;
        }

        const { classes, advisor, customers, productName } = this.props;

        return (
            <div>
                {this.state.notification ? (
                    <Notification
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transitionDuration={0}
                        autoHideDuration={0}
                        notification={this.state.notification}
                    />
                ) : null}
                <form onSubmit={this.onSubmit.bind(this)}>
                    <Grid container={true} spacing={32}>
                        <Grid item={true} sm={12}>
                            <QuotationFormAdvisor
                                classes={classes}
                                productName={productName}
                                advisor={advisor}
                                quotation={this.state.quotation}
                                onChange={this.onAdvisorChange.bind(this)}
                            />
                            <div className={classes.squareBoxLine} />
                        </Grid>
                        <Grid item={true} sm={12}>
                            <QuotationFormCustomer
                                classes={this.props.classes}
                                customers={customers}
                                customer={this.state.customer}
                                productName={productName}
                                onChange={this.onCustomerChange.bind(this)}
                            />
                        </Grid>
                        {this.state.customer && (
                            <Grid item={true} sm={12}>
                                <QuotationFormDate
                                    classes={classes}
                                    customer={this.state.customer}
                                    productName={productName}
                                    quotation={this.state.quotation}
                                    getAgeAtDate={this.productHelper.getAgeAtDate}
                                    onChange={this.onDateChange.bind(this)}
                                />
                            </Grid>
                        )}
                        <Grid item={true} sm={12}>
                            {this.renderProductForm()}
                        </Grid>
                    </Grid>
                    <Grid container={true} spacing={32} style={{ paddingTop: theme.spacing.unit * 4 }}>
                        <Grid item={true} sm={12} style={{ textAlign: "center" }}>
                            <Button
                                type="submit"
                                variant="outlined"
                                disabled={
                                    isNil(this.state.customer) ||
                                    isNil(this.state.quotation) ||
                                    !isNil(this.state.notification)
                                }
                                style={{ width: "12rem", height: "4rem", borderRadius: "0px", borderColor: "#31190c" }}
                            >
                                Valider
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </div>
        );
    }

    private showError(message: string) {
        if (!this.state.notification) {
            this.setState({
                notification: { message: message, code: "400", variant: "error" },
            });
        }
    }

    private hideError() {
        if (this.state.notification) {
            this.setState({
                notification: null,
            });
        }
    }

    private async init() {
        this.productHelper = getProductHelper(this.props.productName, this.props.productVariantName);
        const quotation = Quotation.createNew({
            validityDate: moment()
                // .add(1, "month")
                .format("YYYY-MM-DD"),
            effectiveDate: moment()
                .add(1, "month")
                .format("YYYY-MM-01"),
            creationDate: moment().toISOString(),
            productName: this.props.productName,
            productVariantName: this.props.productVariantName,
            product: (this.productHelper as any).defaultValue,
            actionHistory: [
                {
                    date: moment().toISOString(),
                    action: "creation",
                    type: "proposition",
                    documents: [],
                },
            ],
        });

        await this.productHelper.init(this.props.advisor, null, quotation);

        this.setState({ quotation, customer: null });
    }
}

export default withStyles(quotationFormStyles)(QuotationForm);



// WEBPACK FOOTER //
// ./src/components/QuotationForm/QuotationForm.tsx